
import OrderProductImage from '@marketplace/components/account/orders/parts/order-product-image.vue'
import BaseModal from '@marketplace/components/BaseModal.vue'
import ProductCardInformation from '@marketplace/components/cards/product-card-information.vue'
import SelectInput from '@marketplace/components/forms/inputs/select-input.vue'
import { __normalizeDiscounts } from '@marketplace/components/product/discounts.js'
import ProductCartActions from '@marketplace/components/product-page/parts/product-cart-actions.vue'
import Loader from '@marketplace/components/shared/loaders/loader.vue'
import { QUICKBUY_LISTING_QUERY } from '@marketplace/graphql/listing.js'
import { getFirstNode } from '@marketplace/helpers/data.js'
import { calculateActualMinimumQuantity } from '@marketplace/helpers/quantity-calculations.js'
import {
    trackBuyQuickBuyModal,
    trackOpenQuickBuyModal,
} from '@marketplace/helpers/segment/index.js'
import { DISCOUNTED_PRICE_CALCULATOR } from '@marketplace/mixins/helpers.js'
import { mapGetters } from 'vuex'

export default {
    components: {
        BaseModal,
        Loader,
        OrderProductImage,
        ProductCardInformation,
        SelectInput,
        ProductCartActions,
    },

    props: {
        listingId: {
            type: [String, Number],
            default: '',
        },
        trackingCategory: {
            type: String,
            default: '',
        },
        index: {
            type: Number,
            default: -1,
        },
        isFavorite: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            listing: null,
            discount: null,
            form: {
                variantId: null,
                quantity: 1,
            },
        }
    },

    async fetch() {
        const { data } = await this.$apollo.query({
            query: QUICKBUY_LISTING_QUERY,
            fetchPolicy: 'no-cache',
            variables: {
                listingId: this.listingId,
            },
        })

        const listing = data?.listing

        if (!listing) {
            const error = new Error('Quickbuy modal failed to load')

            this.$sentry.withScope((scope) => {
                scope.setTag('operation', 'quickbuy modal')

                scope.setContext('extraContext', {
                    variables: {
                        listingId: this.listingId,
                    },
                    data,
                    type: this.type,
                })

                this.$sentry.captureException(error)
            })

            throw error
        }
        this.listing = listing

        // Find the first variant that's actually available for purchase
        const availableVariant = listing?.variants?.find((variant) => {
            if (variant?.inventoryPolicy === 'CONTINUE') {
                return true
            }

            const inventory = variant?.inventory ?? 0
            if (inventory <= 0) {
                return false
            }

            const actualMinimum = calculateActualMinimumQuantity(
                listing?.minimumOrderQuantity || 1,
                listing?.caseQuantity || 1
            )

            return inventory >= actualMinimum
        })

        this.form.variantId = availableVariant?.id || listing?.variants?.[0]?.id

        this.form.quantity = calculateActualMinimumQuantity(
            listing?.minimumOrderQuantity || 1,
            listing?.caseQuantity || 1
        )

        trackOpenQuickBuyModal({
            listingId: this.listing.databaseId,
            product: this.listing.title,
            storefront: this.storefront?.name || '',
            category: this.trackingCategory || '',
        })
    },

    computed: {
        ...mapGetters('global', ['userCountry']),

        storefront() {
            return this.listing?.storefront
        },

        image() {
            if (this.listing?.images.edges) {
                return getFirstNode(this.listing.images)?.x1
            }

            return null
        },

        hasVariants() {
            return this.listing?.variants?.length > 1
        },

        variant() {
            return this.listing?.variants.find(
                (variant) => variant.id === this.form.variantId
            )
        },

        availableDiscounts() {
            return __normalizeDiscounts({
                product: this.listing,
                storefront: this.storefront,
            })
        },

        isMobile() {
            return this.$breakpoint.includes('mobile')
        },

        displayPrice() {
            const discount = this.discount?.percentage || 0

            return this.calculateDiscountedPriceWithQuantity(
                this.variant.price,
                discount,
                this.form.quantity
            )
        },

        shipsToCountry() {
            if (!this.storefront?.shippingCountries) {
                return false
            }
            return this.storefront.shippingCountries.includes(this.userCountry)
        },

        isCurrentVariantOutOfStock() {
            if (!this.variant) return true

            const inventory = this.variant?.inventory ?? 0
            const inventoryPolicy = this.variant?.inventoryPolicy

            if (inventoryPolicy === 'CONTINUE') {
                return false
            }

            if (inventory <= 0) {
                return true
            }

            const actualMinimum = calculateActualMinimumQuantity(
                this.listing?.minimumOrderQuantity || 1,
                this.listing?.caseQuantity || 1
            )

            const isOutOfStock = inventory < actualMinimum

            return isOutOfStock
        },

        hasAnyVariantsInStock() {
            if (!this.listing?.variants) return false

            return this.listing.variants.some((variant) => {
                if (variant?.inventoryPolicy === 'CONTINUE') {
                    return true
                }

                const inventory = variant?.inventory ?? 0
                if (inventory <= 0) {
                    return false
                }

                const actualMinimum = calculateActualMinimumQuantity(
                    this.listing?.minimumOrderQuantity || 1,
                    this.listing?.caseQuantity || 1
                )

                return inventory >= actualMinimum
            })
        },

        variantsAsOptions() {
            const variants = this.listing?.variants || []

            return variants
                .filter((variant) => {
                    if (variant?.inventoryPolicy === 'CONTINUE') {
                        return true
                    }

                    const inventory = variant?.inventory ?? 0
                    if (inventory <= 0) {
                        return false
                    }

                    const minimumOrderQuantity = Math.max(
                        1,
                        this.listing?.minimumOrderQuantity || 1
                    )
                    const caseQuantity = Math.max(
                        1,
                        this.listing?.caseQuantity || 1
                    )

                    const actualMinimum = calculateActualMinimumQuantity(
                        minimumOrderQuantity,
                        caseQuantity
                    )

                    return inventory >= actualMinimum
                })
                .map((variant) => {
                    return {
                        value: variant.id,
                        label: variant.title,
                    }
                })
        },
    },

    watch: {
        'form.variantId'() {
            const minimumOrderQuantity = Math.max(
                1,
                this.listing?.minimumOrderQuantity || 1
            )
            const caseQuantity = Math.max(1, this.listing?.caseQuantity || 1)

            const minimumQuantity = calculateActualMinimumQuantity(
                minimumOrderQuantity,
                caseQuantity
            )

            if (this.form.quantity < minimumQuantity) {
                this.form.quantity = minimumQuantity
            }
        },
    },

    methods: {
        ...DISCOUNTED_PRICE_CALCULATOR,

        discountChanged(payload) {
            this.discount = payload
        },

        updateCurrentQuantity(value) {
            this.form.quantity = value
        },

        trackCartSuccess() {
            trackBuyQuickBuyModal({
                name: 'in Modal',
                listingId: this.listing.databaseId,
                category: this.trackingCategory || '',
                brandSlug: this.storefront?.slug || '',
                isFavorite: this.isFavorite,
            })
        },
    },
}
